// src/pages/job_openings/create/AssessmentQuestions/Containers/Questions/Containers/Components/SelectedQuestionContent.tsx
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
import { Button } from '../../../../../../../../ui';
import { UploadMedia } from '../../../../../../../../ui/collapsible_question/upload_media/upload_media';
import { CustomSelect } from '../../../../../../../../ui/custom_react_select/custom_select';
import { MinuteSecondsInput } from '../../../../../../../../ui/input/minute_seconds_input/minute_seconds_input';
import { TextArea } from '../../../../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../../../../ui/input/text_input/text_input';
import {
  convertFileToBase64,
  validateQuestionMedia,
} from '../../../../../../../../utils/helpers/fileHandler';
import { Question } from '../../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../../utils/types/react_select_types';

type Props = {
  toggledQuestion: Question;
  isDisabled: boolean;
  setIsDisabled: (value: boolean) => void;
  editQuestion: (question: Question) => void;
  onAddNewSkill: (
    skill: string,
    parent?: string | null,
    level?: string
  ) => void;
  returnSelectedJobTitles: () => SelectOptions[];
  skills: SelectOptions[];
  subSkills1: SelectOptions[];
  subSkills2: SelectOptions[];
  subSkills3: SelectOptions[];
  levelList?: SelectOptions[];
  jobTitleList: SelectOptions[];
  questionTypes: SelectOptions[];
};

export const SelectedQuestionContent: FC<Props> = ({
  toggledQuestion,
  isDisabled,
  setIsDisabled,
  editQuestion,
  onAddNewSkill,
  returnSelectedJobTitles,
  skills,
  subSkills1,
  subSkills2,
  subSkills3,
  levelList,
  jobTitleList,
  questionTypes,
}) => {
  // 1) Local copy of the question
  const [localQ, setLocalQ] = useState<Question>(toggledQuestion);

  // 2) Reset when parent prop changes
  useEffect(() => {
    setLocalQ(toggledQuestion);
  }, [toggledQuestion]);

  // 3a) Text & number inputs
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setLocalQ((q) => ({
      ...q,
      [name]: name === 'time' || name === 'points' ? Number(value) : value,
    }));
  };

  // 3b) Single-select (skill, level, etc.)
  const handleSelectChange = (selected: SelectOptions | null, action: any) => {
    const { name } = action;
    setLocalQ((q) => ({
      ...q,
      [name]: selected ? selected.value : null,
      // clear dependent subs if needed
      ...(name === 'skillId' && {
        subSkill1Id: null,
        subSkill2Id: null,
        subSkill3Id: null,
      }),
      ...(name === 'subSkill1Id' && {
        subSkill2Id: null,
        subSkill3Id: null,
      }),
      ...(name === 'subSkill2Id' && { subSkill3Id: null }),
    }));
  };

  // 3c) Multi-select (jobTitle[])
  const handleMultiSelectChange = (selected: SelectOptions[], action: any) => {
    setLocalQ((q) => ({
      ...q,
      [action.name]: selected.map((o) => o.value),
    }));
  };

  // 3d) Media upload
  const addFileViaInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !validateQuestionMedia(file)) return;

    convertFileToBase64(file).then((base64) => {
      setLocalQ((q) => ({
        ...q,
        mediaQuestion: true,
        mediaExtension: file.name.split('.').pop() || null,
        fileEncoded: base64 as string, // explicitly cast to string
      }));
    });
  };

  const clearMediaFile = () => {
    setLocalQ((q) => ({
      ...q,
      mediaQuestion: false,
      mediaExtension: null,
      fileEncoded: undefined,
    }));
  };

  // 4) Render
  return (
    <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          editQuestion(localQ);
          setIsDisabled(true);
        }}
        className='grid w-full grid-cols-1 gap-3 md:grid-cols-2'
    >
      <div className='flex flex-col gap-3'>
        <TextInput
            type='text'
            label='Question'
            name='question'
            value={localQ.question}
            onChange={handleChange}
            disabled={isDisabled}
        />

        <TextArea
            label='Answer'
            name='answer'
            value={localQ.answer}
            onChange={handleChange}
            disabled={isDisabled}
        />

        <CustomSelect
            label='Job Title'
            name='jobTitle'
            value={returnSelectedJobTitles()}
            options={jobTitleList}
            onChange={handleMultiSelectChange}
            disabled={isDisabled}
            isMulti
        />

        <CustomSelect
            label='Question Type'
            name='questionType'
            value={
              questionTypes.find((o) => o.value === localQ.questionType) ||
            undefined
            }
            options={questionTypes}
            onChange={handleSelectChange}
            disabled={isDisabled}
        />

        <CustomSelect
            label='Level'
            name='levelId'
            value={
              levelList?.find((o) => o.value === localQ.levelId) || undefined
            }
            options={levelList}
            onChange={handleSelectChange}
            disabled={isDisabled}
        />
      </div>

      <div className='flex flex-col gap-3'>
        <UploadMedia
            question={localQ}
            addFileViaInput={addFileViaInput}
            clearMediaFile={clearMediaFile}
        />

        <CustomSelect
            label='Skill'
            name='skillId'
            value={skills.find((o) => o.value === localQ.skillId) || undefined}
            options={skills}
            onChange={handleSelectChange}
            disabled={isDisabled}
            creatable
            onCreateOption={(input) => onAddNewSkill(input)}
        />

        { localQ.skillId && (
          <CustomSelect
              label='Sub-Skill 1'
              name='subSkill1Id'
              value={
                subSkills1.find((o) => o.value === localQ.subSkill1Id) ||
              undefined
              }
              options={subSkills1}
              onChange={handleSelectChange}
              disabled={isDisabled}
              creatable
              clearable
              onCreateOption={(input) =>
                onAddNewSkill(input, localQ.skillId, '1')
              }
          />
        ) }

        { localQ.subSkill1Id && (
          <CustomSelect
              label='Sub-Skill 2'
              name='subSkill2Id'
              value={
                subSkills2.find((o) => o.value === localQ.subSkill2Id) ||
              undefined
              }
              options={subSkills2}
              onChange={handleSelectChange}
              disabled={isDisabled}
              creatable
              clearable
              onCreateOption={(input) =>
                onAddNewSkill(input, localQ.subSkill1Id, '2')
              }
          />
        ) }

        { localQ.subSkill2Id && (
          <CustomSelect
              label='Sub-Skill 3'
              name='subSkill3Id'
              value={
                subSkills3.find((o) => o.value === localQ.subSkill3Id) ||
              undefined
              }
              options={subSkills3}
              onChange={handleSelectChange}
              disabled={isDisabled}
              creatable
              clearable
              onCreateOption={(input) =>
                onAddNewSkill(input, localQ.subSkill2Id, '3')
              }
          />
        ) }

        <MinuteSecondsInput
            label='Timer (Seconds)'
            name='time'
            value={localQ.time}
            onChange={handleChange}
            disabled={isDisabled}
        />

        <TextInput
            label='Points for Question'
            name='points'
            type='number'
            value={String(localQ.points)}
            onChange={handleChange}
            disabled={isDisabled}
        />
      </div>

      { !isDisabled && (
        <div className='md:col-span-2 flex justify-end border-t border-slate-100 pt-2'>
          <Button
              type='button'
              onClick={() => {
                editQuestion(localQ);
                setIsDisabled(true);
              }}
              label='Save Question'
              variant='primary'
              className='min-h-9 w-full px-3 py-1.5 text-sm sm:w-auto'
          />
        </div>
      ) }
    </form>
  );
};
